import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Wallet, CalendarRange, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
const blobErr = async (err, fallback) => {
  try { return JSON.parse(await err?.response?.data?.text())?.detail || fallback; } catch { return fallback; }
};

/** Self-service: the signed-in staff member's own finalized payslips (matched by account email). */
export function MyPayslipsCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["my-payslips"], queryFn: api.myPayslips });
  const [year, setYear] = useState("");
  const ack = useMutation({ mutationFn: (runId) => api.acknowledgeMyPayslip(runId), onSuccess: () => { toast.success("Thanks — marked as received"); qc.invalidateQueries({ queryKey: ["my-payslips"] }); }, onError: (e) => toast.error(e?.response?.data?.detail || "Could not acknowledge") });
  if (!data?.employee || data.rows.length === 0) return null;
  const y = year || data.years[0];
  const yearPdf = () => api.openMyYearSummaryPdf(y).catch(async (e) => toast.error(await blobErr(e, "Could not open the year summary")));
  return (
    <div className="border border-border rounded-md bg-white mb-4" data-testid="my-payslips">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-border">
        <Wallet className="size-4 text-primary" />
        <span className="overline">My payslips</span>
        <span className="text-xs text-muted-foreground">{data.employee.name}{data.employee.position ? ` · ${data.employee.position}` : ""}</span>
        <div className="ml-auto flex items-center gap-2">
          <Select value={y} onValueChange={setYear}>
            <SelectTrigger data-testid="my-payslips-year" className="h-8 w-24 mono text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{data.years.map((yy) => <SelectItem key={yy} value={yy}>{yy}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" data-testid="my-year-summary-btn" onClick={yearPdf}><CalendarRange className="size-3.5 mr-1.5" /> Year summary PDF</Button>
        </div>
      </div>
      <ul className="divide-y divide-border max-h-52 overflow-y-auto" data-testid="my-payslips-list">
        {data.rows.filter((r) => r.month.startsWith(y)).map((r) => (
          <li key={r.run_id} className="flex items-center gap-3 px-4 py-2 text-sm" data-testid={`my-payslip-${r.run_id}`}>
            <span className="font-medium w-40">{monthLabel(r.month)}</span>
            <span className="text-xs text-muted-foreground">Gross <b className="mono text-foreground">{fmtMoney(r.gross)}</b> · INSS <b className="mono text-foreground">{fmtMoney(r.inss_employee)}</b></span>
            <span className="ml-auto mono text-sm font-semibold">{fmtMoney(r.net_pay)}</span>
            {r.acknowledged_at ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid={`my-payslip-acked-${r.run_id}`}><CheckCircle2 className="size-3.5" /> Received</span>
              : <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`my-payslip-ack-${r.run_id}`} disabled={ack.isPending} onClick={() => ack.mutate(r.run_id)}>Received</Button>}
            <Button size="sm" variant="ghost" className="size-8 p-0" data-testid={`my-payslip-pdf-${r.run_id}`} aria-label="Payslip PDF" onClick={() => api.openMyPayslipPdf(r.run_id, r.month).catch(async (e) => toast.error(await blobErr(e, "Could not open payslip")))}><FileText className="size-4" /></Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
